import { useEffect, useState } from "react";

import { usersService } from "@/modules/dashboard/users/services/users.service";

import type { RolePrincipalRef } from "../types/access.types";

/** How many member avatars the editor previews before collapsing into "+N". */
export const ROLE_MEMBERS_PREVIEW_LIMIT = 8;

export interface RoleMembersState {
  /** Users holding the role — a preview, capped at {@link ROLE_MEMBERS_PREVIEW_LIMIT}. */
  members: RolePrincipalRef[];
  /** How many USERS hold it in total, per the backend's count for the filter. */
  usersTotal: number;
  isLoading: boolean;
  /** The lookup failed; the list is unknown rather than empty. */
  hasFailed: boolean;
}

/** A result, tagged with the role it belongs to so a stale one is never shown for a different role. */
interface RoleMembersResult extends RoleMembersState {
  roleId: string;
}

const CLOSED: RoleMembersState = { members: [], usersTotal: 0, isLoading: false, hasFailed: false };
const LOADING: RoleMembersState = { ...CLOSED, isLoading: true };

/**
 * The users holding one role, fetched lazily when the editor opens.
 *
 * Only USERS. `GET /internal/users?role_id=` exists, but there is no equivalent for API keys — `/api-keys`
 * accepts nothing but `skip`/`limit` — so a role's API-key holders cannot be listed at all. That is why the
 * editor shows `role.member_count` (users **and** keys) as the authoritative headline and treats this list as
 * a labelled preview of just the user half. Presenting this list as "the members" would under-report.
 *
 * The loading and closed states are DERIVED from `roleId` rather than stored, so the effect never calls
 * setState synchronously — the result is tagged with its role and simply ignored once it no longer matches.
 */
export function useRoleMembers(roleId: string | null): RoleMembersState {
  const [result, setResult] = useState<RoleMembersResult | null>(null);

  useEffect(() => {
    if (!roleId) return;
    let cancelled = false;
    usersService
      .fetchList({ role_id: roleId, limit: ROLE_MEMBERS_PREVIEW_LIMIT, skip: 0 })
      .then((response) => {
        if (cancelled) return;
        setResult({
          roleId,
          members: response.data.map((user) => ({
            id: user.id,
            name: user.full_name,
            kind: "person" as const,
          })),
          usersTotal: response.total,
          isLoading: false,
          hasFailed: false,
        });
      })
      .catch(() => {
        // Unknown, not empty: an empty list would claim nobody holds the role.
        if (!cancelled) setResult({ roleId, ...CLOSED, hasFailed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [roleId]);

  if (!roleId) return CLOSED;
  return result?.roleId === roleId ? result : LOADING;
}
